import { getOrganizationSelectionUrl } from "@/auth";
import { ExternalRedirect } from "@/components/ExternalRedirect";
import { FullpageErrorDisplay } from "@/components/FullpageErrorDisplay";
import { useActiveOrganizationQuery } from "@/hooks/useActiveOrganizationQuery";
import { useTranslation } from "react-i18next";
import { OrganizationContext } from "./OrganizationContext";
import { OrganizationLoadingFallback } from "./OrganizationLoadingFallback";

export function OrganizationContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    data: organization,
    isPending,
    isError,
    error,
  } = useActiveOrganizationQuery();
  const { t } = useTranslation();

  if (isPending) {
    return <OrganizationLoadingFallback />;
  }

  if (isError) {
    // if the error is that org could not be found, redirect to org selection
    if (error.response?.status === 404) {
      // redirect to portal backend to identify organization and then come back to frontend afterwards.
      // window.location.search already includes the leading "?" — this provider
      // sits outside the RouterProvider, so there is no router state to read.
      const redirectUrl = window.location.pathname + window.location.search;
      return (
        <ExternalRedirect url={getOrganizationSelectionUrl(redirectUrl)} />
      );
    }

    // else, just show the error
    return (
      <FullpageErrorDisplay
        title={t("organization.error_loading")}
        error={error}
      />
    );
  }

  return (
    <OrganizationContext.Provider value={organization}>
      {children}
    </OrganizationContext.Provider>
  );
}
